/**
 * Injectable Drive actions for the shell's StatusPill. The cloud workstream
 * registers real implementations at startup (`registerDriveActions`); until
 * then every action falls back to navigating to Mer › Lagring › Google Drive, which is
 * always a correct, gesture-preserving thing to do.
 *
 * `signIn` must call `requestAccessToken` synchronously inside the click, so
 * implementations may not await anything before that call.
 */
import type { Uuid } from '@/domain/types';

export interface DriveActions {
  /** Connect Drive for a company: token + ensureCompanyTree + first backup. */
  connect?: (companyId: Uuid) => void | Promise<void>;
  /** Re-authenticate (token expired / needsSignIn). */
  signIn?: (companyId: Uuid) => void | Promise<void>;
  /** Manual backup now. */
  backupNow?: (companyId: Uuid) => void | Promise<void>;
  /** Retry after `lastError`. */
  retry?: (companyId: Uuid) => void | Promise<void>;
}

let actions: DriveActions = {};
const listeners = new Set<() => void>();

export function registerDriveActions(next: DriveActions): void {
  actions = { ...actions, ...next };
  for (const l of listeners) l();
}

export function getDriveActions(): DriveActions {
  return actions;
}

export function subscribeDriveActions(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
